import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { VITE_SUPABASE_URL, GOOGLE_OAUTH_REDIRECT_URI } from "@/utils/const";

import { motion } from "framer-motion";
import { CalendarSelectionModal } from "../CalendarSelectionModal";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Calendar, Check, ExternalLink, RefreshCw } from "lucide-react";

import styles from "./onboarding.module.css";

interface CalendarStepProps {
	onNext: () => void;
	onBack: () => void;
	initialData?: {
		calendarId?: string;
		calendarName?: string;
	};
}

interface Calendar {
	id: string;
	name: string;
	primary: boolean;
	accessRole: string;
}

export const CalendarStep = ({
	onNext,
	onBack,
	initialData,
}: CalendarStepProps) => {
	const [connected, setConnected] = useState(!!initialData?.calendarId);
	const [calendarId, setCalendarId] = useState<string>(
		initialData?.calendarId || "",
	);
	const [calendarName, setCalendarName] = useState<string>(
		initialData?.calendarName || "Primary Calendar",
	);
	const [showCalendarModal, setShowCalendarModal] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleGoogleConnect = async () => {
		setLoading(true);
		setError(null);

		try {
			// Call edge function to get authorization URL
			const response = await fetch(
				`${VITE_SUPABASE_URL}/functions/v1/google-oauth-initiate`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify({
						redirect_uri: GOOGLE_OAUTH_REDIRECT_URI || "https://www.gaiabirth.com/oauth/callback",
					}),
				},
			);

			const result = await response.json();

			if (!result.success) {
				throw new Error(result.error || "Failed to initiate OAuth");
			}

			// Open OAuth popup with specific features to avoid COOP issues
			const popup = window.open(
				result.data.auth_url,
				"google-oauth",
				"width=500,height=600,scrollbars=yes,resizable=yes,status=yes,location=yes",
			);

			if (!popup) {
				throw new Error("Popup blocked. Please allow popups for this site.");
			}

			// Listen for OAuth completion with better error handling
			const handleOAuthComplete = (event: MessageEvent) => {
				if (event.origin !== window.location.origin) {
					return;
				}

				if (event.data.type === "GOOGLE_OAUTH_SUCCESS") {
					// Don't set connected yet - show calendar selection first
					setCalendarId(event.data.calendarId || "primary");
					setCalendarName(event.data.calendarName || "Primary Calendar");
					setLoading(false);
					// Show calendar selection modal
					setShowCalendarModal(true);
					try {
						popup?.close();
					} catch (e) {
						// Ignore COOP errors when closing popup
						console.warn("Failed to close popup:", e);
					}
					window.removeEventListener("message", handleOAuthComplete);
				} else if (event.data.type === "GOOGLE_OAUTH_ERROR") {
					setError("Failed to connect Google Calendar. Please try again.");
					setLoading(false);
					try {
						popup?.close();
					} catch (e) {
						// Ignore COOP errors when closing popup
					}
					window.removeEventListener("message", handleOAuthComplete);
				}
			};

			window.addEventListener("message", handleOAuthComplete);

			// Fallback: check if popup was closed manually (with COOP-safe approach)
			const checkClosed = setInterval(() => {
				try {
					if (popup?.closed) {
						clearInterval(checkClosed);
						window.removeEventListener("message", handleOAuthComplete);
						if (!connected) {
							setLoading(false);
						}
					}
				} catch (e) {
					// COOP blocks access to popup.closed, so we'll rely on the message event
					// This is expected behavior and not an error
				}
			}, 2000);

			// Timeout after 5 minutes
			setTimeout(
				() => {
					clearInterval(checkClosed);
					window.removeEventListener("message", handleOAuthComplete);
					try {
						popup?.close();
					} catch (e) {
						// Ignore COOP errors
					}
					if (!connected) {
						setError("OAuth timeout. Please try again.");
						setLoading(false);
					}
				},
				5 * 60 * 1000,
			);
		} catch (error) {
			console.error("OAuth initiation failed:", error);
			setError("Failed to connect Google Calendar. Please try again.");
			setLoading(false);
		}
	};

	async function handleCalendarSelect(calendar: Calendar) {
		setLoading(true);
		setError(null);

		try {
			// Update the calendar selection in the database
			const response = await fetch(
				`${VITE_SUPABASE_URL}/functions/v1/update-doula-profile`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify({
						step: "calendar",
						data: {
							calendar: {
								calendarId: calendar.id,
								calendarName: calendar.name,
							},
						},
					}),
				},
			);

			const result = await response.json();

			if (!result.success) {
				throw new Error(result.error || "Failed to save calendar selection");
			}

			setCalendarId(calendar.id);
			setCalendarName(calendar.name);
			setConnected(true);
			setShowCalendarModal(false);
			setLoading(false);
		} catch (error) {
			console.error("Failed to save calendar selection:", error);
			setError("Failed to save calendar selection. Please try again.");
			setLoading(false);
		}
	};

	const handleSync = async () => {
		setLoading(true);
		setError(null);

		try {
			const response = await fetch(
				`${VITE_SUPABASE_URL}/functions/v1/google-calendar-sync`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
				},
			);

			const result = await response.json();

			if (!result.success) {
				throw new Error(result.error || "Failed to sync calendar");
			}
		} catch (error) {
			console.error("Calendar sync failed:", error);
			setError("Failed to sync calendar. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	const handleSubmit = async () => {
		if (!connected) {
			setError("Please connect your Google Calendar first.");
			return;
		}

		try {
			// Update doula profile with calendar info
			const response = await fetch(
				`${VITE_SUPABASE_URL}/functions/v1/update-doula-profile`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify({
						step: "calendar",
						data: {
							calendar: {
								calendarId,
								calendarName,
							},
						},
					}),
				},
			);

			const result = await response.json();

			if (!result.success) {
				throw new Error(result.error || "Failed to save calendar settings");
			}

			onNext();
		} catch (error) {
			console.error("Failed to save calendar settings:", error);
			setError("Failed to save settings. Please try again.");
		}
	};

	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.5 }}
			className={styles.wrapperNarrow}
		>
			{/* Progress Bar */}
			<div className={styles.progressSection}>
				<div className={styles.progressTrack}>
					<div className={styles.progressFill} style={{ width: "80%" }} />
				</div>
				<p className={styles.progressNote}>Step 11 of 15</p>
			</div>

			{/* Main Card */}
			<Card className={styles.card}>
				<CardHeader className="text-center pb-4">
					<CardTitle className={styles.cardTitle}>
						<Calendar className={styles.cardIcon} />
						Google Calendar Integration
					</CardTitle>
					<p className={styles.cardSubtitle}>
						Connect your calendar to automatically sync your availability and
						manage bookings.
					</p>
				</CardHeader>
				<CardContent className="space-y-6">
					{error && (
						<Alert variant="destructive">
							<AlertDescription className="font-karla">{error}</AlertDescription>
						</Alert>
					)}

					{!connected ? (
						<div className="text-center space-y-4">
							<div className={styles.innerPanelLg}>
								<svg className="w-32 h-auto mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">
									<path fill="#fff" d="m152.632 47.368-47.368-5.263-57.895 5.263L42.105 100l5.263 52.632L100 159.211l52.632-6.579 5.263-53.947z" />
									<path fill="#1a73e8" d="M68.961 129.026c-3.934-2.658-6.658-6.539-8.145-11.671l9.132-3.763q1.243 4.737 4.342 7.342c2.053 1.737 4.553 2.592 7.474 2.592q4.48 0 7.697-2.724c3.217-2.724 3.224-4.132 3.224-6.934q0-4.303-3.395-7.026c-3.395-2.723-5.105-2.724-8.5-2.724h-5.276v-9.039h4.736q4.382 0 7.382-2.368c2-1.579 3-3.737 3-6.487 0-2.447-.895-4.395-2.684-5.855s-4.053-2.197-6.803-2.197c-2.684 0-4.816.711-6.395 2.145s-2.724 3.197-3.447 5.276l-9.039-3.763c1.197-3.395 3.395-6.395 6.618-8.987s7.342-3.895 12.342-3.895c3.697 0 7.026.711 9.974 2.145 2.947 1.434 5.263 3.421 6.934 5.947 1.671 2.539 2.5 5.382 2.5 8.539 0 3.224-.776 5.947-2.329 8.184s-3.461 3.947-5.724 5.145v.539a17.4 17.4 0 0 1 7.342 5.724c1.908 2.566 2.868 5.632 2.868 9.211s-.908 6.776-2.724 9.579-4.329 5.013-7.513 6.618C89.355 132.184 85.763 133 81.776 133c-4.618.013-8.881-1.316-12.815-3.974M125 83.711l-9.974 7.25-5.013-7.605L128 70.382h6.895v61.197H125z" />
									<path fill="#ea4335" d="M152.632 200 200 152.632l-23.684-10.526-23.684 10.526-10.526 23.684z" />
									<path fill="#34a853" d="M36.842 176.316 47.368 200h105.263v-47.368H47.368z" />
									<path fill="#4285f4" d="M15.789 0C7.066 0 0 7.066 0 15.789v136.842l23.684 10.526 23.684-10.526V47.368h105.263l10.526-23.684L152.632 0z" />
									<path fill="#188038" d="M0 152.632v31.579C0 192.935 7.066 200 15.789 200h31.579v-47.368z" />
									<path fill="#fbbc04" d="M152.632 47.368v105.263H200V47.368l-23.684-10.526z" />
									<path fill="#1967d2" d="M200 47.368V15.789C200 7.065 192.934 0 184.211 0h-31.579v47.368z" />
								</svg>
								<h3 className="text-lg font-semibold text-newTheme-black mb-2 font-karla">
									Connect Your Google Calendar
								</h3>
								<p className="text-newTheme-black/60 mb-6 font-karla">
									This will allow us to sync your availability and help families
									book appointments with you.
								</p>
								<Button
									onClick={handleGoogleConnect}
									disabled={loading}
									className={`w-full ${styles.submitButton}`}
									size="lg"
								>
									{loading ? (
										<>
											<RefreshCw className="h-4 w-4 mr-2 animate-spin" />
											Connecting...
										</>
									) : (
										<>
											<ExternalLink className="h-4 w-4 mr-2" />
											Connect Google Calendar
										</>
									)}
								</Button>
							</div>
						</div>
					) : (
						<div className="space-y-4">
							<div className="bg-green-50 border border-green-200 rounded-lg p-4">
								<div className="flex items-center justify-between">
									<div className="flex items-center gap-3">
										<Check className="h-5 w-5 text-green-600" />
										<div>
											<p className="font-medium text-newTheme-black font-karla">
												Connected to Google Calendar
											</p>
											<p className="text-sm text-newTheme-black/60 font-karla">
												{calendarName}
											</p>
										</div>
									</div>
									<Button
										variant="outline"
										size="sm"
										onClick={() => setShowCalendarModal(true)}
										className={styles.backButton}
									>
										Change Calendar
									</Button>
								</div>
							</div>

							<div className={styles.infoAlert}>
								<h4 className="font-medium text-newTheme-spruce mb-2 font-karla">
									What happens next?
								</h4>
								<ul className={`space-y-1 ${styles.infoText}`}>
									<li>Your calendar will be synced automatically</li>
									<li>Families can see your availability when booking</li>
									<li>New bookings will be added to your calendar</li>
									<li>You can change your calendar anytime</li>
								</ul>
							</div>

							<Button
								onClick={handleSync}
								disabled={loading}
								variant="outline"
								className={`w-full ${styles.backButton}`}
							>
								{loading ? (
									<>
										<RefreshCw className="h-4 w-4 mr-2 animate-spin" />
										Syncing...
									</>
								) : (
									<>
										<RefreshCw className="h-4 w-4 mr-2" />
										Sync Calendar Now
									</>
								)}
							</Button>
						</div>
					)}
				</CardContent>
			</Card>

			{/* Navigation */}
			<div className={styles.navRow}>
				<Button
					variant="outline"
					onClick={onBack}
					className={styles.backButton}
				>
					Back
				</Button>
				<Button
					onClick={handleSubmit}
					disabled={!connected || loading}
					className={styles.submitButton}
				>
					{loading ? (
						<div className="flex items-center">
							<div className={styles.spinner} />
							Saving...
						</div>
					) : (
						"Continue"
					)}
				</Button>
			</div>

			{/* Calendar Selection Modal */}
			<CalendarSelectionModal
				isOpen={showCalendarModal}
				onClose={() => setShowCalendarModal(false)}
				onSelect={handleCalendarSelect}
				currentCalendarId={calendarId}
			/>
		</motion.div>
	);
};
